/**
 * CommuteInvite.js — Pendler-Coach Magic-Link Landing
 * ────────────────────────────────────────────────────
 * Public preview + authenticated acceptance for commute invites generated
 * by the Orb agent's `create_commute_invite` tool.
 *
 *   GET  /api/commute/invite/<token>          → public preview
 *   POST /api/commute/invite/<token>/accept   → authenticated acceptance
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import {
  MapPin, Calendar, Clock, ArrowRight, CheckCircle, AlertCircle,
  UserCircle2, Sparkles, Loader2,
} from 'lucide-react';
import { API } from '../utils/apiConfig';

export default function CommuteInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/commute/invite/${token}`);
        if (!cancelled) setInvite(r.data);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || 'invite_not_found');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const handleAccept = async () => {
    const authToken = localStorage.getItem('token');
    if (!authToken) {
      navigate(`/login?next=/commute/${token}`);
      return;
    }
    setAccepting(true);
    try {
      const r = await axios.post(
        `${API}/commute/invite/${token}/accept`, {},
        { headers: { Authorization: `Bearer ${authToken}` } },
      );
      setAccepted(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'accept_failed');
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div data-testid="commute-loading" style={s.page}>
        <Loader2 size={28} className="spin" style={{ color: '#a855f7' }} />
      </div>
    );
  }

  if (error || !invite) {
    return (
      <div data-testid="commute-error" style={s.page}>
        <div style={s.card}>
          <AlertCircle size={36} color="#ef4444" />
          <h2 style={s.title}>{t('commute.notFoundTitle', 'Einladung nicht gefunden')}</h2>
          <p style={s.desc}>
            {error === 'expired'
              ? t('commute.expired', 'Dieser Link ist abgelaufen. Frag deinen Kollegen nach einem neuen.')
              : t('commute.notFoundDesc', 'Der Link ist ungültig oder wurde bereits genutzt.')}
          </p>
        </div>
      </div>
    );
  }

  const statusBadge = {
    active:  { label: t('commute.status.active',  'Aktiv'),    color: '#22c55e', bg: 'rgba(34,197,94,0.18)' },
    used:    { label: t('commute.status.used',    'Bereits angenommen'), color: '#94a3b8', bg: 'rgba(148,163,184,0.18)' },
    expired: { label: t('commute.status.expired', 'Abgelaufen'), color: '#ef4444', bg: 'rgba(239,68,68,0.18)' },
  }[invite.status] || { label: invite.status, color: '#64748b', bg: 'rgba(100,116,139,0.18)' };

  return (
    <div data-testid="commute-invite-page" style={s.page}>
      <div style={s.card}>
        <div style={s.brandStripe}>
          <Sparkles size={16} color="#a855f7" />
          <span>VoiceRide · Pendler-Empfehlung</span>
        </div>

        <div style={s.senderRow}>
          <UserCircle2 size={42} color="#cbd5e1" />
          <div>
            <div style={{ fontSize: 12, color: '#94a3b8' }}>
              {t('commute.from', 'Empfehlung von')}
            </div>
            <div style={{ fontSize: 17, fontWeight: 700 }}>
              {invite.sender_name || t('commute.unknownSender', 'einem Pendler')}
            </div>
          </div>
          <span data-testid="commute-status-badge" style={{
            ...s.badge, color: statusBadge.color, background: statusBadge.bg,
            borderColor: statusBadge.color + '55',
          }}>{statusBadge.label}</span>
        </div>

        <div style={s.routeBox}>
          <div style={s.routeRow}>
            <MapPin size={16} color="#a855f7" />
            <span style={s.routeCity}>{invite.from_city || '–'}</span>
            <ArrowRight size={14} color="#64748b" />
            <span style={s.routeCity}>{invite.to_city || '–'}</span>
          </div>
          {invite.return_date && (
            <div style={s.routeRow}>
              <Calendar size={14} color="#94a3b8" />
              <span style={{ color: '#cbd5e1' }}>
                {new Date(invite.return_date).toLocaleDateString('de-DE', {
                  weekday: 'long', day: '2-digit', month: 'long',
                })}
              </span>
            </div>
          )}
          {invite.return_time_of_day && (
            <div style={s.routeRow}>
              <Clock size={14} color="#94a3b8" />
              <span style={{ color: '#cbd5e1' }}>{invite.return_time_of_day}</span>
            </div>
          )}
        </div>

        {accepted ? (
          <div data-testid="commute-accepted" style={s.acceptedBox}>
            <CheckCircle size={24} color="#22c55e" />
            <div>
              <div style={{ fontWeight: 700, color: '#bbf7d0', marginBottom: 4 }}>
                {t('commute.acceptedTitle', 'Yes! Du bist drin.')}
              </div>
              <div style={{ fontSize: 13, color: '#cbd5e1' }}>
                {accepted.suggestions?.length > 0
                  ? t('commute.acceptedWithSuggestions', 'Wir haben passende Fahrten für dich gefunden — schau im Orb nach.')
                  : t('commute.acceptedNoSuggestions', 'Sobald jemand diese Strecke fährt, schicken wir dir eine Push-Nachricht.')}
              </div>
            </div>
          </div>
        ) : invite.status === 'active' ? (
          <button
            data-testid="commute-accept-btn"
            onClick={handleAccept}
            disabled={accepting}
            style={{ ...s.cta, opacity: accepting ? 0.6 : 1 }}
          >
            {accepting
              ? <><Loader2 size={16} className="spin" /> {t('common.loading', 'Lade…')}</>
              : <><CheckCircle size={16} /> {t('commute.acceptCta', 'Bin dabei — Empfehlung annehmen')}</>}
          </button>
        ) : null}

        <button
          data-testid="commute-orb-cta"
          onClick={() => navigate('/orb-chat')}
          style={s.secondaryCta}
        >
          {t('commute.openOrbCta', 'Mit Orb sprechen')}
        </button>
      </div>
    </div>
  );
}


// ── Styles ────────────────────────────────────────────────────────
const s = {
  page: {
    minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'radial-gradient(circle at 30% 0%, #1e1b4b 0%, #0b1220 60%, #020617 100%)',
    color: '#f1f5f9', padding: 18,
  },
  card: {
    width: '100%', maxWidth: 460,
    background: 'rgba(15,23,42,0.7)', backdropFilter: 'blur(18px)',
    border: '1px solid rgba(255,255,255,0.08)', borderRadius: 22,
    padding: 26, boxShadow: '0 24px 60px rgba(0,0,0,0.45)',
    display: 'flex', flexDirection: 'column', gap: 18,
  },
  brandStripe: {
    display: 'flex', alignItems: 'center', gap: 6,
    fontSize: 11, color: '#a3a3a3', textTransform: 'uppercase', letterSpacing: 1,
  },
  senderRow: { display: 'flex', alignItems: 'center', gap: 12 },
  badge: {
    marginLeft: 'auto', padding: '3px 10px', borderRadius: 999,
    fontSize: 11, fontWeight: 700, border: '1px solid',
  },
  routeBox: {
    background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)',
    borderRadius: 14, padding: '14px 16px',
    display: 'flex', flexDirection: 'column', gap: 10,
  },
  routeRow: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 },
  routeCity: { fontWeight: 700, color: '#f1f5f9' },
  cta: {
    padding: '12px 18px', borderRadius: 14, border: 'none',
    background: 'linear-gradient(135deg,#22c55e,#16a34a)',
    color: '#fff', fontWeight: 700, fontSize: 15, cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
  },
  secondaryCta: {
    padding: '10px 16px', borderRadius: 12, border: '1px solid rgba(168,85,247,0.4)',
    background: 'rgba(168,85,247,0.08)', color: '#e9d5ff',
    fontWeight: 600, fontSize: 13, cursor: 'pointer',
  },
  acceptedBox: {
    display: 'flex', gap: 12, padding: 14,
    background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)',
    borderRadius: 12,
  },
  title: { margin: '8px 0 4px', fontSize: 18, fontWeight: 700 },
  desc: { color: '#94a3b8', fontSize: 14, lineHeight: 1.5, margin: 0 },
};
